import { z } from "zod";
import { BookingStatusSchema } from "./booking";

/**
 * Practitioner Lane (Phase 2e) — mirrors app/schemas/practitioner.py's
 * shapes.
 */

export const PaymentLaneSchema = z.enum(["rental", "hosted_revshare"]);
export type PaymentLane = z.infer<typeof PaymentLaneSchema>;

export const PAYMENT_LANE_LABELS: Record<PaymentLane, string> = {
  rental: "Rental",
  hosted_revshare: "Hosted revenue-share (not implemented)",
};

export const COIStatusSchema = z.enum(["valid", "expiring_soon", "expired", "missing"]);
export type COIStatus = z.infer<typeof COIStatusSchema>;

export const COI_STATUS_LABELS: Record<COIStatus, string> = {
  valid: "COI valid",
  expiring_soon: "COI expiring soon",
  expired: "COI expired",
  missing: "COI missing",
};

export const COI_STATUS_BADGE_VARIANT: Record<COIStatus, "success" | "warning" | "danger"> = {
  valid: "success",
  expiring_soon: "warning",
  expired: "danger",
  missing: "danger",
};

export const CommitmentStatusSchema = z.enum(["active", "fulfilled", "broken"]);
export type CommitmentStatus = z.infer<typeof CommitmentStatusSchema>;

export const COMMITMENT_STATUS_BADGE_VARIANT: Record<CommitmentStatus, "success" | "warning" | "danger"> = {
  active: "success",
  fulfilled: "success",
  broken: "danger",
};

export const PractitionerSchema = z.object({
  id: z.string(),
  contact_id: z.string(),
  contact_name: z.string(),
  business_name: z.string().nullable(),
  coi_provider_name: z.string().nullable(),
  coi_policy_number: z.string().nullable(),
  coi_expiry_date: z.string().nullable(),
  coi_status: COIStatusSchema,
  payment_lane: PaymentLaneSchema,
  active: z.boolean(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type Practitioner = z.infer<typeof PractitionerSchema>;

export const PractitionerListResponseSchema = z.object({ items: z.array(PractitionerSchema) });

export const PractitionerCommitmentSchema = z.object({
  id: z.string(),
  practitioner_id: z.string(),
  start_month: z.string(),
  target_months: z.number(),
  target_sessions_per_month: z.number(),
  discount_pct: z.number(),
  status: CommitmentStatusSchema,
  created_at: z.string(),
});
export type PractitionerCommitment = z.infer<typeof PractitionerCommitmentSchema>;

export const PractitionerBookingSchema = z.object({
  id: z.string(),
  booking_id: z.string(),
  practitioner_id: z.string(),
  resource_id: z.string(),
  resource_name: z.string(),
  booking_date: z.string(),
  status: BookingStatusSchema,
  base_price_cents: z.number(),
  discount_pct: z.number(),
  discount_reason: z.string().nullable(),
  price_cents: z.number(),
  commitment_id: z.string().nullable(),
  invoice_id: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type PractitionerBooking = z.infer<typeof PractitionerBookingSchema>;

export const PractitionerBookingListResponseSchema = z.object({ items: z.array(PractitionerBookingSchema) });

// ---- Mutation inputs --------------------------------------------------------

export interface CreatePractitionerInput {
  contact_id: string;
  business_name?: string | null;
  coi_provider_name?: string | null;
  coi_policy_number?: string | null;
  coi_expiry_date?: string | null;
  payment_lane?: PaymentLane;
  notes?: string | null;
}

export interface UpdatePractitionerInput {
  id: string;
  business_name?: string | null;
  coi_provider_name?: string | null;
  coi_policy_number?: string | null;
  coi_expiry_date?: string | null;
  payment_lane?: PaymentLane;
  active?: boolean;
  notes?: string | null;
}

export interface CreatePractitionerBookingInput {
  practitionerId: string;
  resource_id: string;
  booking_date: string;
  status?: string;
  notes?: string | null;
  /** Consecutive 90-min Aerie blocks this booking covers (Nikki's 8/03
   * reprice) — 2+ earns the 10% discount. Defaults to 1 server-side. */
  blocks_booked?: number;
  /** Whether this weekly-recurring booking is on automatic monthly
   * payment — bumps the 20% recurring discount to 25%. */
  autopay?: boolean;
}

export interface CreateCommitmentInput {
  practitionerId: string;
  start_month: string;
}
